/**
 * Contract for the offline order-pricing engine.
 *
 * Framework-free by design — no Angular, no PouchDB, no Dynamics. The engine is a pure
 * function so it can be proven correct from a fixture table with no network, which is the
 * only way to trust it on a device with no signal.
 *
 * See docs/features/order-capture.md.
 */

import { PricingPriceList } from '../../../interfaces/pricing/pricing-bundle.interface';
import { roundHalfAwayFromZero } from '../../../utility/round.util';

/** One product row of the matrix. */
export interface OrderLineInput {
  /** Product GUID. Matches `PricingItem.product`. */
  product: string;
  /** Quantity per delivery sequence, e.g. `{ 1: 40, 2: 50 }`. Absent sequence === 0. */
  quantities: Record<number, number>;
  /** Rep-entered discount. Percentage OR amount — if both are set, percentage wins. */
  manualDiscountPct?: number | null;
  manualDiscountAmount?: number | null;
}

/** One delivery column. */
export interface DeliveryInput {
  sequence: number;
  date?: string | null;
  shipTo?: string | null;
  /** Where complete-order free goods land; without one, the first delivery. */
  isPrimary?: boolean;
}

export interface PricingInput {
  /**
   * The resolved price list from the cached bundle. Resolution (priority → isDefault) happens
   * before the engine so that pricing stays a pure function of one list.
   */
  priceList: PricingPriceList;
  lines: OrderLineInput[];
  deliveries: DeliveryInput[];

  /**
   * Validity date, ISO `yyyy-MM-dd`. EXPLICIT and never read from the clock inside the engine —
   * device skew would otherwise change prices with no visible cause.
   */
  asOf: string;

  /** Mirrors the bundle request flag. When false, unmapped cross-product offers are dropped. */
  includeUnmapped?: boolean;

  /**
   * The caller's ceiling on a line's total discount; 100 (none) by default. A winning rule's own
   * `thresholdPercentage` with `ThresholdUsage.Cap` also caps it, and the tighter of the two wins.
   */
  maxDiscountPct?: number;

  /**
   * The price list's Max Discount Amount: a ceiling on the whole order's discount, after each line's
   * own. Absent, none; when both limits are set the tighter applies.
   */
  maxDiscountAmount?: number;

  /**
   * Quantity of each product this account has already taken against its quota, from
   * previously submitted orders. The engine cannot know order history, so the caller
   * supplies it; absent means no prior consumption.
   */
  priorQuantityByProduct?: Record<string, number>;

  /** Carried onto the result for provenance, so a submit-time diff is possible. */
  bundleVersion?: string;
}

export type PricingViolationSeverity = 'block' | 'warn';

export type PricingViolationCode =
  | 'priceListNotYetValid'
  | 'priceListExpired'
  | 'productNotOnPriceList'
  | 'itemNotValid'
  | 'belowMinQuantity'
  | 'aboveMaxQuantity'
  | 'quantityNotOnIncrement'
  | 'quotaExceeded'
  | 'overlappingTiers'
  | 'discountClamped'
  | 'orderDiscountCapped'
  | 'negativeLineClamped'
  | 'belowMinOrderValue'
  | 'belowMinOrderQuantity'
  | 'belowMinDeliveryValue'
  | 'belowMinDeliveryQuantity';

export interface PricingViolation {
  code: PricingViolationCode;
  severity: PricingViolationSeverity;
  /** Product GUID when the violation belongs to a line. */
  product?: string;
  /** Delivery sequence when the violation belongs to one drop. */
  sequence?: number;
  /** The configured limit, for a message that states the threshold. */
  limit?: number;
  /** What the order actually has. */
  actual?: number;
  /**
   * i18n key plus its parameters, never a pre-formatted string: the engine is
   * framework-free and the view translates. Keyed by `code` via VIOLATION_MESSAGE_KEYS.
   */
  messageKey: string;
  params: Record<string, string | number>;
}

/** Translation key for each violation, so the vocabulary stays in one place. */
export const VIOLATION_MESSAGE_KEYS: Record<PricingViolationCode, string> = {
  priceListNotYetValid: 'OC_VIOLATION_PRICE_LIST_NOT_YET_VALID',
  priceListExpired: 'OC_VIOLATION_PRICE_LIST_EXPIRED',
  productNotOnPriceList: 'OC_VIOLATION_PRODUCT_NOT_ON_PRICE_LIST',
  itemNotValid: 'OC_VIOLATION_ITEM_NOT_VALID',
  belowMinQuantity: 'OC_VIOLATION_BELOW_MIN_QUANTITY',
  aboveMaxQuantity: 'OC_VIOLATION_ABOVE_MAX_QUANTITY',
  quantityNotOnIncrement: 'OC_VIOLATION_QUANTITY_NOT_ON_INCREMENT',
  quotaExceeded: 'OC_VIOLATION_QUOTA_EXCEEDED',
  overlappingTiers: 'OC_VIOLATION_OVERLAPPING_TIERS',
  discountClamped: 'OC_VIOLATION_DISCOUNT_CLAMPED',
  orderDiscountCapped: 'OC_VIOLATION_ORDER_DISCOUNT_CAPPED',
  negativeLineClamped: 'OC_VIOLATION_NEGATIVE_LINE_CLAMPED',
  belowMinOrderValue: 'OC_VIOLATION_BELOW_MIN_ORDER_VALUE',
  belowMinOrderQuantity: 'OC_VIOLATION_BELOW_MIN_ORDER_QUANTITY',
  belowMinDeliveryValue: 'OC_VIOLATION_BELOW_MIN_DELIVERY_VALUE',
  belowMinDeliveryQuantity: 'OC_VIOLATION_BELOW_MIN_DELIVERY_QUANTITY',
};

export type LineOfferKind = 'discount' | 'freeGood';

/** `earned` has already been applied to this result; `available` is a nudge, not a promise. */
export type LineOfferStatus = 'earned' | 'available';

/**
 * What a line has earned, and what it is close to earning.
 *
 * The matrix renders these as chips ("90 units combined → +5 free", "+10 more across
 * deliveries → +5 free") and the toolbar digest lists the earned ones. Deciding what is
 * within reach needs the rule bands, so it belongs to the engine, not the view.
 */
export interface LineOffer {
  kind: LineOfferKind;
  status: LineOfferStatus;
  ruleId: string;
  /** Whether `threshold` and `away` count this product's units, a basket's, or the whole order's. */
  measure: 'quantity' | 'basket' | 'order';
  /** The band or qualifying quantity this offer sits on. */
  threshold: number | null;
  /** How much more is needed. Only set when `status` is `available`. */
  away: number | null;
  /** Set for a discount offer. */
  percentage: number | null;
  /** The free-goods reward, or the product a discount lands on (null: the basket or this row). */
  rewardProduct: string | null;
  /** A product the offer needs that is not ordered yet. */
  requiredProduct?: string;
  rewardUnits: number | null;
  /** The delivery a split-order shortfall is measured on; absent when measured on the whole order. */
  sequence?: number;
}

export interface PricedCell {
  sequence: number;
  quantity: number;
  gross: number;
  net: number;
  /** The percentage this delivery got; differs between deliveries under a split-order rule. */
  discountPct?: number;
}

export interface PricedLine {
  product: string;
  priceListItemId: string | null;
  productCategory: string | null;
  unitPrice: number;

  /** Combined across every delivery — the quantity discounts are evaluated on. */
  quantity: number;
  perDelivery: PricedCell[];

  gross: number;
  volumeDiscountPct: number;
  manualDiscountPct: number;
  /** After summing and clamping at `maxDiscountPct`. */
  effectiveDiscountPct: number;
  net: number;

  /** Reward units earned. Extra goods — they never reduce `net`. */
  freeUnits: number;
  /** True on a generated reward line. */
  isFreeGood: boolean;

  /** Ids of every rule that fired. The audit trail, persisted to the order line. */
  appliedRuleIds: string[];
  /** Earned offers plus the next one within reach. Drives the row chips and the digest. */
  offers: LineOffer[];
  /** Which scope discounts were evaluated on, so the assumption is visible downstream. */
}

export interface PricedDelivery {
  sequence: number;
  quantity: number;
  gross: number;
  net: number;
}

export interface PricedOrder {
  currency: string;
  /** Decimals every amount here is rounded to: the currency's (KWD 3), as SaveOrder checks them. */
  currencyPrecision: number;
  priceListId: string;
  bundleVersion: string | null;
  asOf: string;

  lines: PricedLine[];
  deliveries: PricedDelivery[];

  gross: number;
  net: number;
  totalQuantity: number;
  lineCount: number;
  deliveryCount: number;

  violations: PricingViolation[];
  /** True when no `block` violation is present. */
  canSubmit: boolean;
  /** Which rules and free goods produced the money, for `indskr_SaveOrder`'s `appliedDiscounts`. */
  firings: PricedFiring[];
}

/** A line's part in a firing: it earned it (Q), received it (R), or both (B). */
export interface FiringAllocation {
  product: string;
  sequence: number;
  /** The generated reward line, not the bought line of the same product and delivery. */
  freeGood: boolean;
  role: 'Q' | 'R' | 'B';
  quantity: number;
  amount: number;
}

/**
 * One rule or free-goods offer that applied: the grain of an `indskr_applieddiscount` record.
 * Its R and B allocations sum to `amount` to the cent, which the server checks; the server also
 * stamps each line's `indskr_applieddiscountrule` from the lowest-sequence firing it received.
 */
export interface PricedFiring {
  ruleId: string | null;
  freeGoodId: string | null;
  listId: string | null;
  /** Null for an order-level rule naming no product, which discounts every line. */
  productId: string | null;
  baseAmount: number;
  amount: number;
  /** What it asked for before a ceiling cut it; null when nothing was cut. */
  uncappedAmount: number | null;
  evaluatedQuantity: number;
  rewardQuantity: number | null;
  repeatCount: number | null;
  qualifyingRuleIds: string[];
  qualifyingFreeGoodIds: string[];
  allocations: FiringAllocation[];
}

/** A part of a firing that took money off a bought line: not a free good, not a qualifier's share. */
export function isMoneyAllocation(part: FiringAllocation): boolean {
  return !part.freeGood && part.role !== 'Q' && part.amount > 0;
}

export function isSet<T>(value: T | null | undefined): value is T {
  return value !== null && value !== undefined;
}

/** The share of a gross the discount takes, as the engine states percentages. */
export function percentOff(gross: number, net: number): number {
  return gross > 0 ? round2(((gross - net) / gross) * 100) : 0;
}

/** Rounds a percentage to two places, half away from zero (2.135 → 2.14). Money uses `roundMoney`. */
export function round2(value: number): number {
  return roundHalfAwayFromZero(value, 2);
}

export const DEFAULT_CURRENCY_PRECISION = 2;

/** A list's currency precision, 0–4 as Dynamics allows; 2 when the bundle predates the field. */
export function currencyPrecisionOf(
  list: Pick<PricingPriceList, 'currencyPrecision'> | null | undefined,
): number {
  const precision = list?.currencyPrecision;
  return isSet(precision) &&
    Number.isInteger(precision) &&
    precision >= 0 &&
    precision <= 4
    ? precision
    : DEFAULT_CURRENCY_PRECISION;
}

/** Money to its currency's decimals, half away from zero as SaveOrder rounds (0.3745 KWD → 0.375). */
export function roundMoney(value: number, precision: number): number {
  return roundHalfAwayFromZero(value, precision);
}

/** In the currency's smallest unit (cents, fils), as an integer. */
export function minorUnits(value: number, precision: number): number {
  return Math.round(value * 10 ** precision);
}
